import React, { FC, memo, ReactNode } from 'react';
import { ButtonProps } from 'antd/lib/button';
import { ProfileOwnerExcerptDTO } from 'models/user';
import { Button, message, Modal, Tooltip } from 'antd';
import {
  ExclamationCircleOutlined,
  MinusSquareOutlined,
} from '@ant-design/icons/lib';
import { useMutation } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { PartnerHigoDeviceExcerpt } from 'models/higoDevice';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface UnassignUserPartnerDeviceButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartnerId: number;
  device: PartnerHigoDeviceExcerpt;
  user: ProfileOwnerExcerptDTO;
  tooltip?: ReactNode;
  onUnassign?: () => void;
}

export const UnassignUserPartnerDeviceButton: FC<UnassignUserPartnerDeviceButtonProps> = memo(
  ({
    businessPartnerId,
    device,
    user,
    onUnassign,
    tooltip,
    ...buttonProps
  }) => {
    const [mutate, { isLoading }] = useMutation(
      BusinessPartnersAPI.unassignUserDevice
    );
    const intlMessage = useFormatMessage();

    const showConfirm = () =>
      Modal.confirm({
        title: intlMessage(
          { id: 'devices.unassign.user.confirm' },
          {
            serialNumber: device.serialNumber,
            firstName: user.firstName,
            lastName: user.lastName,
          }
        ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            { businessPartnerId, userId: user.userId!, deviceId: device.id },
            {
              onSuccess: () => {
                message.success(
                  intlMessage(
                    { id: 'devices.unassign.user.success' },
                    {
                      serialNumber: device.serialNumber,
                      firstName: user.firstName,
                      lastName: user.lastName,
                    }
                  )
                );
                onUnassign && onUnassign();
              },
            }
          ),
      });

    const button = (
      <Button
        id="button-unassign-user-device"
        onClick={showConfirm}
        loading={isLoading}
        icon={<MinusSquareOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
